import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/authContext";
import Sidebar from "../pages/Sidebar";
import { ChatDockProvider } from '../contexts/ChatDockContext.jsx';
import ChatDock from '../components/chatdock/ChatDock.jsx';
import MessengerPanel from '../pages/Messenger/MessengerPanel';
import { MessagesUnreadProvider } from '../contexts/MessagesUnreadContext.jsx';
import { CallProvider } from '../contexts/CallContext.jsx';
import CallOverlay from '../components/CallOverlay/CallOverlay.jsx';
import { MediaSettingsProvider } from '../contexts/MediaSettingsContext.jsx';
import MediaSettingsModal from '../components/MediaSettings/MediaSettingsModal.jsx';
import GlobalReportModal from '../components/GlobalReportModal.jsx';
import Header from "../pages/Header";
import SettingsModal from '../modals/SettingsModal';
import NotificationsPanel from '../panels/NotificationsPanel';
import Home from '../pages/Home/Home';
import Profile from '../pages/Profile/Profile';
import Groups from '../pages/Groups/Groups';
import Events from '../pages/Events/Events';
import Discover from '../pages/Discover/Discover';
import Academy from '../pages/Academy/AcademyLandingPage';
import AcademyLMSLayout from '../pages/Academy/Academylms/GlobalLMSLayout/AcademyLMSLayout';
import VirtualCafe from '../pages/VirtualCafe/VirtualCafe';
import Catalog from '../pages/eBookstore/Catalog.jsx';
import Product from '../pages/eBookstore/Product.jsx';
import Library from '../pages/eBookstore/Library.jsx';
import PublisherOnboarding from '../pages/eBookstore/publisher/Onboarding.jsx';
import PublisherDashboard from '../pages/eBookstore/publisher/Dashboard.jsx';
import AdminSubmissions from '../pages/eBookstore/admin/Submissions.jsx';
import AboutUs from '../pages/AboutUs/AboutUs';
import ProtectedRoute from '../components/ProtectedRoute';
import { Outlet } from 'react-router-dom';
import './AuthenticatedLayout.css';
import LmsSidebar from '../pages/Academy/Academylms/GlobalLMSLayout/LmsSidebar';
import CourseDetail from '../pages/Academy/Academylms/Courses/CourseDetail';
import LessonDetail from '../pages/Academy/Academylms/Lessons/LessonDetail';
import AssignmentDetail from '../pages/Academy/Academylms/Assignments/AssignmentDetail';
import ReportsPanel from '../pages/Academy/Academylms/common/ReportsPanel.jsx';
import CourseManagementPanel from '../pages/Academy/Academylms/common/CourseManagementPanel.jsx';
import StudentCoursesPanel from '../pages/Academy/Academylms/common/StudentCoursesPanel.jsx';
import ErrorBoundary from '../components/ErrorBoundary.jsx';
import Onboarding from '../pages/Onboarding/Onboarding.jsx';
import { HeaderActionsProvider } from '../contexts/HeaderActionsContext.jsx';

// Dashboard Components (using lazy-loaded versions below)

// Shared Components (will be added as needed for additional routes)

// Fix: Use React.lazy for heavy/dynamically loaded components
const AdminNotificationPanelContainer = React.lazy(() => import('../pages/Academy/Academylms/common/AdminNotificationPanelContainer.jsx'));
const CourseBuilderStudio = React.lazy(() => import('../pages/Academy/Academylms/coursebuilder/CourseBuilderStudio.jsx'));
const AnalyticsDashboard = React.lazy(() => import('../pages/Academy/Academylms/common/AnalyticsDashboard.jsx'));
const IntegrationsPanel = React.lazy(() => import('../pages/Academy/Academylms/IntegrationsPanel.jsx'));
const StorageManagement = React.lazy(() => import('../pages/Academy/Academylms/Dashboards/SuperAdminDashboard/components/StorageManagement.jsx'));
const ModerationPanel = React.lazy(() => import('../pages/Academy/Academylms/common/ModerationPanel.jsx'));

import CourseBuilderSkeleton from '../pages/Academy/Academylms/coursebuilder/CourseBuilderSkeleton.jsx';
import '../pages/Academy/Academylms/coursebuilder/CourseBuilderStudio.css';
import '../pages/Academy/Academylms/coursebuilder/CourseDashboard.css';
import '../pages/Academy/Academylms/coursebuilder/HierarchicalOutlineBuilder.css';

// Superadmin Management Pages
const SuperadminUserDetail = React.lazy(() => import('../pages/Academy/Academylms/Superadmin/Users/UserDetail.jsx'));
const SuperadminCourseDetail = React.lazy(() => import('../pages/Academy/Academylms/Superadmin/Courses/CourseDetail.jsx'));
const SuperadminCourseEdit = React.lazy(() => import('../pages/Academy/Academylms/Superadmin/Courses/CourseEdit.jsx'));

// Role-aware wrapper components
import * as analyticsApi from '../api/academyAnalytics';
import supabase from '../supabaseClient';
const SuperAdminAnalytics = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    async function fetchAnalytics() {
      setLoading(true);
      try {
        const [roleCounts, courseCount, assignmentCount, assessmentCount, avgRating, recentActivity] = await Promise.all([
          analyticsApi.getRoleCounts(),
          analyticsApi.getCourseCount(),
          analyticsApi.getAssignmentCount(),
          analyticsApi.getAssessmentCount(),
          analyticsApi.getAverageCourseRating(),
          analyticsApi.getRecentActivity()
        ]);
        setAnalytics({
          progress: 100,
          progressBreakdown: {
            students: roleCounts.student || 0,
            teachers: roleCounts.teacher || 0,
            designers: roleCounts.designer || 0,
            managers: roleCounts.manager || 0,
            admins: roleCounts.admin || 0,
            courses: courseCount || 0,
            assignments: assignmentCount || 0,
            assessments: assessmentCount || 0,
            avgRating: avgRating || 'N/A'
          },
          skills: [
            { label: 'User Engagement', value: (roleCounts.student || 0) + (roleCounts.teacher || 0) },
            { label: 'Course Coverage', value: courseCount },
            { label: 'Satisfaction', value: avgRating }
          ],
          activities: recentActivity.map(act => ({
            type: act.action_type,
            label: act.details?.label || act.action_type,
            date: act.created_at?.split('T')[0]
          }))
        });
      } catch (err) {
        setAnalytics(null);
      }
      setLoading(false);
    }
    fetchAnalytics();
  }, []);
  if (loading || !analytics) return <div>Loading analytics...</div>;
  return <AnalyticsDashboard user={user} role="superadmin" analytics={analytics} />;
};

// Redirects /app/profile to /app/profile/:currentUserId for shareable self profile links
function ProfileSelfRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/app/home" replace />;
  return <Navigate to={`/app/profile/${user.id}`} replace />;
}

const AdminAnalytics = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    async function fetchAnalytics() {
      setLoading(true);
      try {
        const [roleCounts, courseCount, assignmentCount, assessmentCount, recentActivity] = await Promise.all([
          analyticsApi.getRoleCounts(),
          analyticsApi.getCourseCount(),
          analyticsApi.getAssignmentCount(),
          analyticsApi.getAssessmentCount(),
          analyticsApi.getRecentActivity()
        ]);
        setAnalytics({
          progress: 92,
          progressBreakdown: {
            users: Object.values(roleCounts).reduce((a, b) => a + b, 0),
            courses: courseCount || 0,
            assignments: assignmentCount || 0,
            assessments: assessmentCount || 0
          },
          skills: [
            { label: 'User Management', value: roleCounts.admin || 0 },
            { label: 'Reporting', value: 80 },
            { label: 'Audit', value: 75 }
          ],
          activities: recentActivity.map(act => ({
            type: act.action_type,
            label: act.details?.label || act.action_type,
            date: act.created_at?.split('T')[0]
          }))
        });
      } catch (err) {
        setAnalytics(null);
      }
      setLoading(false);
    }
    fetchAnalytics();
  }, []);
  if (loading || !analytics) return <div>Loading analytics...</div>;
  return <AnalyticsDashboard user={user} role="admin" analytics={analytics} />;
};

const ManagerAnalytics = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    async function fetchAnalytics() {
      setLoading(true);
      try {
        // Only courses managed by this manager
        const { data: courses, error: courseErr } = await supabase
          .from('academy_courses')
          .select('id')
          .eq('manager_id', user.id);
        if (courseErr) throw courseErr;
        const courseIds = courses.map(c => c.id);
        // Count assignments/assessments for these courses
        const [{ count: assignmentCount }, { count: assessmentCount }] = await Promise.all([
          supabase
            .from('academy_assignments')
            .select('id', { count: 'exact', head: true })
            .in('course_id', courseIds),
          supabase
            .from('academy_assessments')
            .select('id', { count: 'exact', head: true })
            .in('course_id', courseIds)
        ]);
        const { count: studentCount } = await supabase
          .from('academy_enrollments')
          .select('id', { count: 'exact', head: true })
          .in('course_id', courseIds);
        const recentActivity = await analyticsApi.getRecentActivity();
        setAnalytics({
          progress: 85,
          progressBreakdown: {
            courses: courseIds.length,
            assignments: assignmentCount || 0,
            assessments: assessmentCount || 0,
            students: studentCount || 0
          },
          skills: [
            { label: 'Leadership', value: 90 },
            { label: 'Assessment', value: assignmentCount || 0 },
            { label: 'Planning', value: assessmentCount || 0 }
          ],
          activities: recentActivity.map(act => ({
            type: act.action_type,
            label: act.details?.label || act.action_type,
            date: act.created_at?.split('T')[0]
          }))
        });
      } catch (err) {
        setAnalytics(null);
      }
      setLoading(false);
    }
    fetchAnalytics();
  }, [user]);
  if (loading || !analytics) return <div>Loading analytics...</div>;
  return <AnalyticsDashboard user={user} role="manager" analytics={analytics} />;
};

const TeacherAnalytics = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    async function fetchAnalytics() {
      setLoading(true);
      try {
        // Only courses taught by this teacher
        const { data: courses, error: courseErr } = await supabase
          .from('academy_courses')
          .select('id')
          .eq('teacher_id', user.id);
        if (courseErr) throw courseErr;
        const courseIds = courses.map(c => c.id);
        // Lessons and assignments for these courses
        const [{ count: lessonCount }, { count: assignmentCount }] = await Promise.all([
          supabase
            .from('academy_lessons')
            .select('id', { count: 'exact', head: true })
            .in('module_id', courseIds),
          supabase
            .from('academy_assignments')
            .select('id', { count: 'exact', head: true })
            .in('course_id', courseIds)
        ]);
        const { count: studentCount } = await supabase
          .from('academy_enrollments')
          .select('id', { count: 'exact', head: true })
          .in('course_id', courseIds);
        const recentActivity = await analyticsApi.getRecentActivity();
        setAnalytics({
          progress: 76,
          progressBreakdown: {
            courses: courseIds.length,
            lessons: lessonCount || 0,
            assignments: assignmentCount || 0,
            students: studentCount || 0
          },
          skills: [
            { label: 'Teaching', value: 92 },
            { label: 'Grading', value: 85 },
            { label: 'Lesson Planning', value: lessonCount || 0 }
          ],
          activities: recentActivity.map(act => ({
            type: act.action_type,
            label: act.details?.label || act.action_type,
            date: act.created_at?.split('T')[0]
          }))
        });
      } catch (err) {
        setAnalytics(null);
      }
      setLoading(false);
    }
    fetchAnalytics();
  }, [user]);
  if (loading || !analytics) return <div>Loading analytics...</div>;
  return <AnalyticsDashboard user={user} role="teacher" analytics={analytics} />;
};

const StudentAnalytics = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    async function fetchAnalytics() {
      setLoading(true);
      try {
        // Only enrollments and assignments for this student
        const { count: courseCount } = await supabase
          .from('academy_enrollments')
          .select('id', { count: 'exact', head: true })
          .eq('student_id', user.id);
        const { count: assignmentCount } = await supabase
          .from('academy_submissions')
          .select('id', { count: 'exact', head: true })
          .eq('student_id', user.id);
        const { count: quizCount } = await supabase
          .from('academy_assessment_results')
          .select('id', { count: 'exact', head: true })
          .eq('student_id', user.id);
        const recentActivity = await analyticsApi.getRecentActivity();
        setAnalytics({
          progress: 63,
          progressBreakdown: {
            courses: courseCount || 0,
            assignments: assignmentCount || 0,
            quizzes: quizCount || 0
          },
          skills: [
            { label: 'Reading', value: 70 },
            { label: 'Writing', value: 65 },
            { label: 'Speaking', value: 60 }
          ],
          activities: recentActivity.map(act => ({
            type: act.action_type,
            label: act.details?.label || act.action_type,
            date: act.created_at?.split('T')[0]
          }))
        });
      } catch (err) {
        setAnalytics(null);
      }
      setLoading(false);
    }
    fetchAnalytics();
  }, [user]);
  if (loading || !analytics) return <div>Loading analytics...</div>;
  return <AnalyticsDashboard user={user} role="student" analytics={analytics} />;
};

const DesignerAnalytics = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    async function fetchAnalytics() {
      setLoading(true);
      try {
        // Example: designer's created courses and templates
        const { count: courseCount } = await supabase
          .from('academy_courses')
          .select('id', { count: 'exact', head: true })
          .eq('created_by', user.id);
        // If you have a templates table, fetch here; else use courses designed as proxy
        const recentActivity = await analyticsApi.getRecentActivity();
        setAnalytics({
          progress: 88,
          progressBreakdown: {
            coursesDesigned: courseCount || 0
          },
          skills: [
            { label: 'Creativity', value: 95 },
            { label: 'Course Design', value: courseCount || 0 },
            { label: 'Collaboration', value: 85 }
          ],
          activities: recentActivity.map(act => ({
            type: act.action_type,
            label: act.details?.label || act.action_type,
            date: act.created_at?.split('T')[0]
          }))
        });
      } catch (err) {
        setAnalytics(null);
      }
      setLoading(false);
    }
    fetchAnalytics();
  }, [user]);
  if (loading || !analytics) return <div>Loading analytics...</div>;
  return <AnalyticsDashboard user={user} role="designer" analytics={analytics} />;
};

// Teacher LMS feature components
const TeacherDashboard = React.lazy(() => import('../pages/Academy/Academylms/Dashboards/TeacherDashboard/TeacherDashboard.jsx'));
const AssignmentCreator = React.lazy(() => import('../pages/Academy/Academylms/assessment/AssignmentCreator.jsx'));
const AssignmentSubmissionView = React.lazy(() => import('../pages/Academy/Academylms/assessment/AssignmentSubmissionView.jsx'));
const EvaluationPanel = React.lazy(() => import('../pages/Academy/Academylms/assessment/EvaluationPanel.jsx'));
const ForumView = React.lazy(() => import('../pages/Academy/Academylms/social/ForumView.jsx'));
// Additional dashboards
const SuperAdminDashboard = React.lazy(() => import('../pages/Academy/Academylms/Dashboards/SuperAdminDashboard/SuperAdminDashboard.jsx'));
const AdminDashboard = React.lazy(() => import('../pages/Academy/Academylms/Dashboards/AdminDashboard/AdminDashboard.jsx'));
const DesignerDashboard = React.lazy(() => import('../pages/Academy/Academylms/Dashboards/DesignerDashboard/DesignerDashboard.jsx'));
const StudentDashboard = React.lazy(() => import('../pages/Academy/Academylms/Dashboards/StudentDashboard/StudentDashboard.jsx'));
// Virtual Classroom demo
const VirtualClassroomDemo = React.lazy(() => import('../pages/Academy/Academylms/VirtualClassroom/MainRoom.jsx'));


// Manager LMS feature components
const ManagerDashboard = React.lazy(() => import('../pages/Academy/Academylms/Dashboards/ManagerDashboard/ManagerDashboard.jsx'));
const ManagerTeachersPanel = React.lazy(() => import('../pages/Academy/Academylms/common/ManagerTeachersPanel.jsx'));
const ManagerStudentsPanel = React.lazy(() => import('../pages/Academy/Academylms/common/ManagerStudentsPanel.jsx'));
const ManagerAssessmentsPanel = React.lazy(() => import('../pages/Academy/Academylms/common/ManagerAssessmentsPanel.jsx'));
const ManagerGroupsPanel = React.lazy(() => import('../pages/Academy/Academylms/common/ManagerGroupsPanel.jsx'));
const ManagerTeacherAssignment = React.lazy(() => import('../pages/Academy/Academylms/common/ManagerTeacherAssignment.jsx'));

// ...
const SuperAdminLMSMain = React.lazy(() => import('../pages/Academy/Academylms/GlobalLMSLayout/SuperAdminLMSMain.jsx'));
const AdminLMSMain = React.lazy(() => import('../pages/Academy/Academylms/GlobalLMSLayout/AdminLMSMain.jsx'));
const ManagerLMSMain = React.lazy(() => import('../pages/Academy/Academylms/GlobalLMSLayout/ManagerLMSMain.jsx'));
const DesignerLMSMain = React.lazy(() => import('../pages/Academy/Academylms/GlobalLMSLayout/DesignerLMSMain.jsx'));
const TeacherLMSMain = React.lazy(() => import('../pages/Academy/Academylms/GlobalLMSLayout/TeacherLMSMain.jsx'));
const StudentLMSMain = React.lazy(() => import('../pages/Academy/Academylms/GlobalLMSLayout/StudentLMSMain.jsx'));
const StudentCourseSelector = React.lazy(() => import('../pages/Academy/Academylms/student/StudentCourseSelector.jsx'));
const StudentCourseLayout = React.lazy(() => import('../pages/Academy/Academylms/student/StudentCourseLayout.jsx'));
const StudentCourseDashboard = React.lazy(() => import('../pages/Academy/Academylms/student/StudentCourseDashboard.jsx'));
const StudentCourseSocial = React.lazy(() => import('../pages/Academy/Academylms/student/StudentCourseSocial.jsx'));
const TeacherCourseSelector = React.lazy(() => import('../pages/Academy/Academylms/teacher/TeacherCourseSelector.jsx'));
const TeacherCourseLayout = React.lazy(() => import('../pages/Academy/Academylms/teacher/TeacherCourseLayout.jsx'));
const TeacherCourseDashboard = React.lazy(() => import('../pages/Academy/Academylms/teacher/TeacherCourseDashboard.jsx'));
const TeacherCourseSocial = React.lazy(() => import('../pages/Academy/Academylms/teacher/TeacherCourseSocial.jsx'));
const AcademyGroupSocial = React.lazy(() => import('../pages/Academy/Academylms/social/AcademyGroupSocial.jsx'));
const StudentSocialHub = React.lazy(() => import('../pages/Academy/Academylms/social/StudentSocialHub.jsx'));
const TeacherSocialHub = React.lazy(() => import('../pages/Academy/Academylms/social/TeacherSocialHub.jsx'));
const SuperAdminSettings = React.lazy(() => import('../pages/Academy/Academylms/SuperAdminSettings.jsx'));
const SuperAdminSystem = React.lazy(() => import('../pages/Academy/Academylms/SuperAdminSystem.jsx'));
const Roles = React.lazy(() => import('../pages/Academy/Academylms/Roles.jsx'));
const AcademyLogs = React.lazy(() => import('../pages/Academy/Academylms/AcademyLogs.jsx'));
const HelpCenter = React.lazy(() => import('../pages/Academy/Academylms/HelpCenter.jsx'));
const Birthdays = React.lazy(() => import('../pages/Birthdays/Birthdays.jsx'));

const AuthenticatedLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileRailView, setMobileRailView] = useState('main');
  const [isDraggingMobileRailThumb, setIsDraggingMobileRailThumb] = useState(false);
  const [mobileRailThumbProgress, setMobileRailThumbProgress] = useState(null);
  const mobileRailThumbDragRef = useRef({
    pointerId: null,
    startClientX: 0,
    startProgress: 0,
    travelPx: 1,
  });
  const mobileRailToggleRef = useRef(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMessenger, setShowMessenger] = useState(false);
  const [messengerInitialContactId, setMessengerInitialContactId] = useState(null);
  const [activeSection, setActiveSection] = useState('home');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profileChecked, setProfileChecked] = useState(false);
  const [profileComplete, setProfileComplete] = useState(true);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');

    const apply = () => {
      document.body.classList.toggle('auth-mobile-scroll-lock', mq.matches);
    };

    apply();

    if (mq.addEventListener) {
      mq.addEventListener('change', apply);
    } else {
      mq.addListener(apply);
    }

    return () => {
      document.body.classList.remove('auth-mobile-scroll-lock');
      if (mq.removeEventListener) {
        mq.removeEventListener('change', apply);
      } else {
        mq.removeListener(apply);
      }
    };
  }, []);

  useEffect(() => {
    // Set active section based on current path
    const path = location.pathname.split('/')[2] || 'home';
    setActiveSection(path);
    console.log("AuthenticatedLayout mounted, user:", user);
    console.log("Current path:", location.pathname);
  }, [location, user]);

  // Check profile completeness and redirect to onboarding if needed
  useEffect(() => {
    async function checkProfile() {
      if (!user) return;
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('name, display_name, phone, location, date_of_birth, gender, occupation, native_languages, target_languages, bio, avatar_url')
          .eq('id', user.id)
          .single();
        if (error) {
          // If no row yet, treat as incomplete
          setProfileComplete(false);
        } else {
          const complete = !!(
            data?.name &&
            data?.display_name &&
            data?.phone &&
            data?.location &&
            data?.date_of_birth &&
            data?.gender &&
            data?.occupation &&
            Array.isArray(data?.native_languages) && data.native_languages.length > 0 &&
            Array.isArray(data?.target_languages) && data.target_languages.length > 0 &&
            data?.bio &&
            data?.avatar_url
          );
          setProfileComplete(complete);
        }
      } catch (e) {
        setProfileComplete(false);
      } finally {
        setProfileChecked(true);
      }
    }
    if (user) {
      checkProfile();
    }
  }, [user]);

  useEffect(() => {
    if (!user || !profileChecked) return;
    const isOnboarding = location.pathname === '/app/onboarding';
    if (!profileComplete && !isOnboarding) {
      navigate('/app/onboarding', { replace: true });
    }
    if (profileComplete && isOnboarding) {
      navigate('/app/home', { replace: true });
    }
  }, [profileChecked, profileComplete, user, location.pathname, navigate]);

  // Listen for global requests (e.g., from ChatDock) to open Messenger for a contact
  useEffect(() => {
    const handler = (event) => {
      const contactId = event.detail && event.detail.contactId;
      if (contactId) {
        setMessengerInitialContactId(contactId);
      }
      setShowMessenger(true);
    };
    window.addEventListener('virtulingu:openMessenger', handler);
    return () => window.removeEventListener('virtulingu:openMessenger', handler);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleSectionChange = (section) => {
    navigate(`/app/${section}`);
    setActiveSection(section);
  };

  const handleToggleSidebar = (collapsed) => {
    setSidebarCollapsed(collapsed);
  };

  useEffect(() => {
    // Default to main view when route changes (prevents getting stuck on Chat on navigation)
    setMobileRailView('main');
    setMobileRailThumbProgress(null);
  }, [location.pathname]);

  const baseMobileRailProgress = useMemo(() => (mobileRailView === 'chat' ? 1 : 0), [mobileRailView]);
  const effectiveMobileRailProgress = mobileRailThumbProgress == null ? baseMobileRailProgress : mobileRailThumbProgress;

  useEffect(() => {
    if (!isDraggingMobileRailThumb) return;
    const handlePointerMove = (e) => {
      if (mobileRailThumbDragRef.current.pointerId == null) return;
      if (e.pointerId !== mobileRailThumbDragRef.current.pointerId) return;
      const dx = e.clientX - mobileRailThumbDragRef.current.startClientX;
      const next = mobileRailThumbDragRef.current.startProgress + dx / mobileRailThumbDragRef.current.travelPx;
      setMobileRailThumbProgress(Math.max(0, Math.min(1, next)));
    };
    const handlePointerUp = (e) => {
      if (mobileRailThumbDragRef.current.pointerId == null) return;
      if (e.pointerId !== mobileRailThumbDragRef.current.pointerId) return;

      const finalProgress = mobileRailThumbProgress == null ? baseMobileRailProgress : mobileRailThumbProgress;
      const nextView = finalProgress >= 0.5 ? 'chat' : 'main';
      setMobileRailView(nextView);
      setMobileRailThumbProgress(null);
      setIsDraggingMobileRailThumb(false);
      mobileRailThumbDragRef.current.pointerId = null;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [isDraggingMobileRailThumb, mobileRailThumbProgress, baseMobileRailProgress]);

  const onMobileRailThumbPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const root = mobileRailToggleRef.current;
    if (!root) return;

    const rect = root.getBoundingClientRect();
    const pad = 6; // must match CSS
    const travelPx = Math.max(1, rect.width - pad * 2 - (rect.width - pad * 2) / 2);
    const startProgress = mobileRailThumbProgress == null ? baseMobileRailProgress : mobileRailThumbProgress;

    setIsDraggingMobileRailThumb(true);
    setMobileRailThumbProgress(startProgress);

    mobileRailThumbDragRef.current = {
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startProgress,
      travelPx,
    };

    if (e.currentTarget && e.currentTarget.setPointerCapture) {
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // ignore
      }
    }
  };

  const isLMSRoute = location.pathname === '/app/academy/lms' || location.pathname.startsWith('/app/academy/lms/');
  // Extract LMS role from URL (e.g., /app/academy/lms/teacher/...)
  let lmsRole = null;
  if (isLMSRoute) {
    const match = location.pathname.match(/\/app\/academy\/lms\/(\w+)/);
    lmsRole = match ? match[1] : null;
  }

  if (!user) {
    console.log("No user in AuthenticatedLayout");
    return <div className="loading">Loading user data...</div>;
  }

  return (
    <MessagesUnreadProvider>
      <ChatDockProvider>
        <MediaSettingsProvider>
          <CallProvider>
            <HeaderActionsProvider>
              <ErrorBoundary>
                <div className={`auth-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''} auth-mobile-view-${mobileRailView}`}>
                  {isLMSRoute ? (
                    <LmsSidebar onToggleSidebar={handleToggleSidebar} />
                  ) : (
                    <Sidebar 
                      activeSection={activeSection} 
                      onSectionChange={handleSectionChange}
                      onLogout={handleLogout}
                      onToggleSidebar={handleToggleSidebar}
                    />
                  )}
                  
                  {/* Floating sidebar toggle for mobile */}
                  <button 
                    className="mobile-sidebar-toggle"
                    onClick={() => {
                      // Trigger the sidebar's internal toggle via a DOM click on its collapse button
                      const sidebarToggleBtn = document.querySelector('.sidebar .collapse-button');
                      if (sidebarToggleBtn) sidebarToggleBtn.click();
                    }}
                    aria-label="Toggle sidebar"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="3" y1="12" x2="21" y2="12"></line>
                      <line x1="3" y1="6" x2="21" y2="6"></line>
                      <line x1="3" y1="18" x2="21" y2="18"></line>
                    </svg>
                  </button>
                  
                  <div className="auth-main-content">
                    <Header 
                      currentUser={user} 
                      onToggleSettings={() => setShowSettings(true)}
                      onToggleNotifications={() => setShowNotifications(!showNotifications)}
                      onToggleMessenger={() => setShowMessenger(!showMessenger)}
                      onLogout={handleLogout}
                    />
                    <div className="auth-content-area">
                      <main className="page-shell">
                        <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/home" element={<Home />} />
                        <Route path="/profile" element={<ProfileSelfRedirect />} />
                        <Route path="/profile/:id" element={<Profile />} />
                        <Route path="/birthdays" element={<Birthdays />} />
                        <Route path="/groups/*" element={<Groups />} />
                        <Route path="/events/*" element={<Events />} />
                        <Route path="/discover" element={<Discover />} />
                        {/* Messenger no longer has a dedicated page; use the global panel instead */}
                        {/* <Route path="/messenger" element={<Messenger />} /> */}
                        {/* Virtual Cafe inside /app/* (relative aliases) */}
                        <Route path="virtualcafe" element={<VirtualCafe />} />
                        <Route path="virtual-cafe" element={<VirtualCafe />} />
                        <Route path="virtualcafe/:roomId" element={<VirtualCafe />} />
                        <Route path="virtual-cafe/:roomId" element={<VirtualCafe />} />
                        <Route path="/academy" element={<Academy />} />
                      <Route path="/onboarding" element={<Onboarding />} />
                        
                        {/* Academy LMS Routes - Role-based access */}
                        <Route path="/academy/lms" element={
                          <ProtectedRoute allowedRoles={['student', 'teacher', 'designer', 'manager', 'admin', 'superadmin']}>
                            <AcademyLMSLayout />
                          </ProtectedRoute>
                        } />
                        <Route path="/academy/lms/*" element={
                          <ProtectedRoute allowedRoles={['student', 'teacher', 'designer', 'manager', 'admin', 'superadmin']}>
                            <AcademyLMSLayout />
                          </ProtectedRoute>
                        }>
                          {/* SuperAdmin Routes */}
                          <Route path="superadmin" element={<ErrorBoundary><React.Suspense fallback={<div>Loading...</div>}><SuperAdminLMSMain /></React.Suspense></ErrorBoundary>} />
                          <Route path="superadmin/dashboard" element={<ErrorBoundary><React.Suspense fallback={<div>Loading...</div>}><SuperAdminDashboard /></React.Suspense></ErrorBoundary>} />
                          <Route path="superadmin/notifications" element={<ErrorBoundary><React.Suspense fallback={<div>Loading...</div>}><AdminNotificationPanelContainer /></React.Suspense></ErrorBoundary>} />
                          <Route path="superadmin/courses" element={<ErrorBoundary><React.Suspense fallback={<div>Loading...</div>}><CourseManagementPanel role="superadmin" /></React.Suspense></ErrorBoundary>} />
                          <Route path="superadmin/courses/:id" element={<ErrorBoundary><React.Suspense fallback={<div>Loading...</div>}><SuperadminCourseDetail /></React.Suspense></ErrorBoundary>} />
                          <Route path="superadmin/courses/:id/edit" element={<ErrorBoundary><React.Suspense fallback={<div>Loading...</div>}><SuperadminCourseEdit /></React.Suspense></ErrorBoundary>} />
                          <Route path="superadmin/users/:id" element={<ErrorBoundary><React.Suspense fallback={<div>Loading...</div>}><SuperadminUserDetail /></React.Suspense></ErrorBoundary>} />
                          <Route path="superadmin/analytics" element={<ErrorBoundary><React.Suspense fallback={<div>Loading...</div>}><SuperAdminAnalytics /></React.Suspense></ErrorBoundary>} />
                          <Route path="superadmin/storage" element={<ErrorBoundary><React.Suspense fallback={<div>Loading...</div>}><StorageManagement /></React.Suspense></ErrorBoundary>} />
                          <Route path="superadmin/integrations" element={<ErrorBoundary><React.Suspense fallback={<div>Loading...</div>}><IntegrationsPanel /></React.Suspense></ErrorBoundary>} />
                          <Route path="superadmin/settings" element={<ErrorBoundary><React.Suspense fallback={<div>Loading...</div>}><SuperAdminSettings /></React.Suspense></ErrorBoundary>} />
                          <Route path="superadmin/system" element={<ErrorBoundary><React.Suspense fallback={<div>Loading...</div>}><SuperAdminSystem /></React.Suspense></ErrorBoundary>} />
                          <Route path="superadmin/moderation" element={<ErrorBoundary><React.Suspense fallback={<div>Loading...</div>}><ModerationPanel /></React.Suspense></ErrorBoundary>} />
                          <Route path="superadmin/roles" element={<ErrorBoundary><React.Suspense fallback={<div>Loading...</div>}><Roles /></React.Suspense></ErrorBoundary>} />
                          <Route path="superadmin/logs" element={<ErrorBoundary><React.Suspense fallback={<div>Loading...</div>}><AcademyLogs /></React.Suspense></ErrorBoundary>} />
                          <Route path="superadmin/reports" element={<ErrorBoundary><React.Suspense fallback={<div>Loading...</div>}><ReportsPanel role="superadmin" /></React.Suspense></ErrorBoundary>} />
                          <Route path="superadmin/help" element={<ErrorBoundary><React.Suspense fallback={<div>Loading...</div>}><HelpCenter /></React.Suspense></ErrorBoundary>} />
                          
                          {/* Admin Routes */}
                          <Route path="admin" element={<React.Suspense fallback={<div>Loading...</div>}><AdminLMSMain /></React.Suspense>} />
                          <Route path="admin/dashboard" element={<React.Suspense fallback={<div>Loading...</div>}><AdminDashboard /></React.Suspense>} />
                          <Route path="admin/notifications" element={<React.Suspense fallback={<div>Loading...</div>}><AdminNotificationPanelContainer /></React.Suspense>} />
                          <Route path="admin/courses" element={<React.Suspense fallback={<div>Loading...</div>}><CourseManagementPanel role="admin" /></React.Suspense>} />
                          <Route path="admin/analytics" element={<React.Suspense fallback={<div>Loading...</div>}><AdminAnalytics /></React.Suspense>} />
                          <Route path="admin/moderation" element={<React.Suspense fallback={<div>Loading...</div>}><ModerationPanel /></React.Suspense>} />
                          <Route path="admin/reports" element={<React.Suspense fallback={<div>Loading...</div>}><ReportsPanel role="admin" /></React.Suspense>} />
                          <Route path="admin/settings" element={<React.Suspense fallback={<div>Loading...</div>}><SuperAdminSettings /></React.Suspense>} />
                          <Route path="admin/system" element={<React.Suspense fallback={<div>Loading...</div>}><SuperAdminSystem /></React.Suspense>} />
                          <Route path="admin/logs" element={<React.Suspense fallback={<div>Loading...</div>}><AcademyLogs /></React.Suspense>} />
                          <Route path="admin/help" element={<React.Suspense fallback={<div>Loading...</div>}><HelpCenter /></React.Suspense>} />
                          
                          {/* Manager Routes */}
                          <Route path="manager" element={<React.Suspense fallback={<div>Loading...</div>}><ManagerLMSMain /></React.Suspense>} />
                          <Route path="manager/dashboard" element={<React.Suspense fallback={<div>Loading...</div>}><ManagerDashboard /></React.Suspense>} />
                          <Route path="manager/reports" element={<React.Suspense fallback={<div>Loading...</div>}><ReportsPanel role="manager" user={user} /></React.Suspense>} />
                          <Route path="manager/courses" element={<React.Suspense fallback={<div>Loading...</div>}><CourseManagementPanel role="manager" /></React.Suspense>} />
                          <Route path="manager/analytics" element={<React.Suspense fallback={<div>Loading...</div>}><ManagerAnalytics /></React.Suspense>} />
                          <Route path="manager/teachers" element={<React.Suspense fallback={<div>Loading...</div>}><ManagerTeachersPanel /></React.Suspense>} />
                          <Route path="manager/teachers/assign/:courseId" element={<React.Suspense fallback={<div>Loading...</div>}><ManagerTeacherAssignment /></React.Suspense>} />
                          <Route path="manager/students" element={<React.Suspense fallback={<div>Loading...</div>}><ManagerStudentsPanel /></React.Suspense>} />
                          <Route path="manager/assessments" element={<React.Suspense fallback={<div>Loading...</div>}><ManagerAssessmentsPanel /></React.Suspense>} />
                          <Route path="manager/groups" element={<React.Suspense fallback={<div>Loading...</div>}><ManagerGroupsPanel /></React.Suspense>} />
                          
                          {/* Teacher Routes - Course-Scoped Architecture */}
                          <Route path="teacher" element={<React.Suspense fallback={<div>Loading...</div>}><TeacherCourseSelector /></React.Suspense>} />
                          
                          {/* Teacher Course Workspace */}
                          <Route path="teacher/courses/:courseId" element={<React.Suspense fallback={<div>Loading...</div>}><TeacherCourseLayout /></React.Suspense>}>
                            <Route path="dashboard" element={<React.Suspense fallback={<div>Loading...</div>}><TeacherCourseDashboard /></React.Suspense>} />
                            <Route path="social" element={<React.Suspense fallback={<div>Loading...</div>}><TeacherCourseSocial /></React.Suspense>} />
                            <Route path="roster" element={<div className="lms-placeholder">Roster - Coming Soon</div>} />
                            <Route path="assignments" element={<div className="lms-placeholder">Assignments - Coming Soon</div>} />
                            <Route path="grades" element={<div className="lms-placeholder">Grades - Coming Soon</div>} />
                            <Route path="analytics" element={<div className="lms-placeholder">Analytics - Coming Soon</div>} />
                          </Route>
                          
                          {/* Legacy Teacher Routes (for backward compatibility) */}
                          <Route path="teacher/dashboard" element={<React.Suspense fallback={<div>Loading...</div>}><TeacherDashboard /></React.Suspense>} />
                          <Route path="teacher/reports" element={<React.Suspense fallback={<div>Loading...</div>}><ReportsPanel role="teacher" user={user} /></React.Suspense>} />
                          <Route path="teacher/analytics" element={<React.Suspense fallback={<div>Loading...</div>}><TeacherAnalytics /></React.Suspense>} />
                          <Route path="teacher/courses" element={<React.Suspense fallback={<div>Loading...</div>}><CourseManagementPanel role="teacher" /></React.Suspense>} />
                          <Route path="teacher/classroom" element={<React.Suspense fallback={<div>Loading...</div>}><VirtualClassroomDemo /></React.Suspense>} />
                          <Route path="teacher/social" element={<React.Suspense fallback={<div>Loading...</div>}><TeacherSocialHub /></React.Suspense>} />
                          <Route path="teacher/groups/:groupId/social" element={<React.Suspense fallback={<div>Loading...</div>}><AcademyGroupSocial role="teacher" /></React.Suspense>} />
                          
                          {/* Student Routes - Course-Scoped Architecture */}
                          <Route path="student" element={<React.Suspense fallback={<div>Loading...</div>}><StudentCourseSelector /></React.Suspense>} />
                          
                          {/* Student Course Workspace */}
                          <Route path="student/courses/:courseId" element={<React.Suspense fallback={<div>Loading...</div>}><StudentCourseLayout /></React.Suspense>}>
                            <Route path="dashboard" element={<React.Suspense fallback={<div>Loading...</div>}><StudentCourseDashboard /></React.Suspense>} />
                            <Route path="social" element={<React.Suspense fallback={<div>Loading...</div>}><StudentCourseSocial /></React.Suspense>} />
                            <Route path="assignments" element={<div className="lms-placeholder">Assignments - Coming Soon</div>} />
                            <Route path="grades" element={<div className="lms-placeholder">Grades - Coming Soon</div>} />
                            <Route path="classroom" element={<React.Suspense fallback={<div>Loading...</div>}><VirtualClassroomDemo /></React.Suspense>} />
                          </Route>
                          
                          {/* Legacy Student Routes (for backward compatibility) */}
                          <Route path="student/dashboard" element={<React.Suspense fallback={<div>Loading...</div>}><StudentDashboard /></React.Suspense>} />
                          <Route path="student/reports" element={<React.Suspense fallback={<div>Loading...</div>}><ReportsPanel role="student" /></React.Suspense>} />
                          <Route path="student/analytics" element={<React.Suspense fallback={<div>Loading...</div>}><StudentAnalytics /></React.Suspense>} />
                          <Route path="student/courses" element={<React.Suspense fallback={<div>Loading...</div>}><StudentCoursesPanel /></React.Suspense>} />
                          <Route path="student/social" element={<React.Suspense fallback={<div>Loading...</div>}><StudentSocialHub /></React.Suspense>} />
                          <Route path="student/groups/:groupId/social" element={<React.Suspense fallback={<div>Loading...</div>}><AcademyGroupSocial role="student" /></React.Suspense>} />
                          
                          {/* Designer Routes */}
                          <Route path="designer" element={<React.Suspense fallback={<div>Loading...</div>}><DesignerLMSMain /></React.Suspense>} />
                          <Route path="designer/dashboard" element={<React.Suspense fallback={<div>Loading...</div>}><DesignerDashboard /></React.Suspense>} />
                          <Route path="designer/courses" element={<React.Suspense fallback={<CourseBuilderSkeleton />}><CourseBuilderStudio /></React.Suspense>} />
                          <Route path="designer/analytics" element={<React.Suspense fallback={<div>Loading...</div>}><DesignerAnalytics /></React.Suspense>} />
                          <Route path="designer/content" element={<div className="lms-placeholder">Designer Content - Coming Soon</div>} />
                          
                          {/* Deep-linking routes */}
                          <Route path="courses/:courseId" element={<CourseDetail />} />
                          <Route path="lessons/:lessonId" element={<LessonDetail />} />
                          <Route path="assignments/:assignmentId" element={<AssignmentDetail />} />
                        </Route>
                        
                        {/* Backward compatibility redirects removed - now using nested routes */}
                        
                        {/* Virtual Cafe routes (support multiple paths) */}
                        <Route path="/virtual-cafe" element={<VirtualCafe />} />
                        <Route path="/virtual-cafe/:roomId" element={<VirtualCafe />} />
                        <Route path="/app/virtual-cafe" element={<VirtualCafe />} />
                        <Route path="/app/virtualcafe" element={<VirtualCafe />} />
                        <Route path="/app/virtual-cafe/:roomId" element={<VirtualCafe />} />
                        <Route path="/app/virtualcafe/:roomId" element={<VirtualCafe />} />
                        <Route path="/eBookstore" element={<Catalog />} />
                        <Route path="/eBookstore/library" element={<Library />} />
                        <Route path="/eBookstore/:slug" element={<Product />} />
                        <Route path="/eBookstore/publisher/onboarding" element={<PublisherOnboarding />} />
                        <Route path="/eBookstore/publisher/dashboard" element={<PublisherDashboard />} />
                        <Route path="/eBookstore/admin/submissions" element={<AdminSubmissions />} />
                        <Route path="/about" element={<AboutUs />} />
                      </Routes>
                      </main>
                      <aside className="right-rail" aria-label="Supplementary content">
                        <div className="right-rail-section right-rail-top">
                          <div id="page-right-rail-slot" className="right-rail-slot" />
                        </div>
                        <div className="right-rail-section right-rail-bottom">
                          <ChatDock />
                        </div>
                      </aside>
                    </div>

                    <div
                      className="mobile-rail-toggle"
                      ref={mobileRailToggleRef}
                      role="tablist"
                      aria-label="Main or chat view"
                      data-dragging={isDraggingMobileRailThumb ? 'true' : 'false'}
                    >
                      <div className="mobile-rail-toggle-track" />
                      <div
                        className="mobile-rail-toggle-thumb"
                        role="presentation"
                        style={{ transform: `translateX(${effectiveMobileRailProgress * 100}%)` }}
                        onPointerDown={onMobileRailThumbPointerDown}
                        aria-label="Drag to switch between Main and Chat"
                      />
                      <button
                        type="button"
                        className={`mobile-rail-toggle-btn ${mobileRailView === 'main' ? 'active' : ''}`}
                        onClick={() => {
                          setMobileRailView('main');
                          setMobileRailThumbProgress(null);
                        }}
                        role="tab"
                        aria-selected={mobileRailView === 'main'}
                      >
                        Main
                      </button>
                      <button
                        type="button"
                        className={`mobile-rail-toggle-btn ${mobileRailView === 'chat' ? 'active' : ''}`}
                        onClick={() => {
                          setMobileRailView('chat');
                          setMobileRailThumbProgress(null);
                        }}
                        role="tab"
                        aria-selected={mobileRailView === 'chat'}
                      >
                        Chat
                      </button>
                    </div>

                    {showNotifications && (
                      <NotificationsPanel 
                        onClose={() => setShowNotifications(false)} 
                        isOpen={showNotifications}
                      />
                    )}
                    <SettingsModal 
                      isOpen={showSettings} 
                      onClose={() => setShowSettings(false)} 
                      currentUser={user}
                    />

                  </div>
                  
                  {/* Messenger Panel */}
                  <MessengerPanel
                    isOpen={showMessenger}
                    onClose={() => {
                      setShowMessenger(false);
                    }}
                    initialContactId={messengerInitialContactId}
                  />
                  
                  {/* Call Overlay - persistent across all routes */}
                  <CallOverlay />
                  
                  {/* Media Settings Modal - global */}
                  <MediaSettingsModal />
                  
                  {/* Global Report Modal - listens for virtulingu:openReportModal events */}
                  <GlobalReportModal />

                </div>
                </ErrorBoundary>
              </HeaderActionsProvider>
            </CallProvider>
          </MediaSettingsProvider>
        </ChatDockProvider>
      </MessagesUnreadProvider>
  );
};

export default AuthenticatedLayout;
